<template>
  <div ref="rootRef" class="relative" @focusout="onFocusOut">
    <input
      v-if="allowCustom"
      ref="searchInputRef"
      :value="modelValue ? selectedLabel || String(modelValue) : ''"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="open"
      :disabled="disabled"
      :placeholder="placeholder"
      :maxlength="maxlength"
      class="w-full rounded-lg border border-gray-700 bg-bochas-bg px-4 py-3 text-left text-white placeholder-gray-500 transition-colors focus:border-bochas-rojo focus:outline-none disabled:opacity-60"
      @focus="openSelect"
      @input="onCustomInput"
      @keydown.escape="open = false"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter="onCustomEnter"
    />
    <button
      v-else-if="!open || disabled"
      type="button"
      :class="[
        'flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border bg-bochas-bg px-4 py-3 text-left text-white transition-colors focus:outline-none',
        disabled
          ? 'cursor-not-allowed border-gray-700 opacity-60'
          : open
          ? 'border-bochas-rojo'
          : 'border-gray-700 hover:border-gray-500',
      ]"
      :disabled="disabled"
      @click="openSelect"
      @keydown.down.prevent="openSelect"
      @keydown.up.prevent="openSelect"
      @keydown.enter.prevent="openSelect"
      @keydown.space.prevent="openSelect"
    >
      <span :class="selectedLabel ? 'text-white' : 'text-gray-500'">
        {{ selectedLabel || placeholder }}
      </span>
      <IconFbochas name="chevron-down" :size="18" class="flex-shrink-0 text-gray-300" />
    </button>
    <input
      v-else
      ref="searchInputRef"
      v-model="search"
      type="search"
      :class="[
        'w-full rounded-lg border border-bochas-rojo bg-bochas-bg px-4 py-3 text-left text-white placeholder-gray-500 transition-colors focus:outline-none',
      ]"
      :placeholder="selectedLabel || placeholder"
      @keydown.escape="open = false"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter.prevent="selectActiveOption"
    />

    <Transition name="select-fade">
      <div
        v-if="open"
        class="select-field-menu absolute left-0 right-0 z-[80] mt-2 max-h-80 overflow-y-auto rounded-xl border border-white/10 bg-[#1a2030] p-1 shadow-2xl"
      >
        <button
          v-for="(option, index) in filteredOptions"
          :key="option.value"
          type="button"
          :ref="(el) => setOptionRef(el, index)"
          :class="[
            'block w-full whitespace-normal break-words rounded-lg px-3 py-2.5 text-left text-sm leading-snug transition-colors',
            option.value === modelValue
              ? 'bg-bochas-rojo text-white'
              : index === activeIndex
              ? 'bg-white/10 text-white'
              : 'text-white hover:bg-white/10',
          ]"
          @click="selectOption(option.value)"
        >
          {{ option.label }}
        </button>
        <p v-if="filteredOptions.length === 0 && !allowCustom" class="px-3 py-2.5 text-sm text-gray-400">
          Sin resultados
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, required: true },
  placeholder: { type: String, default: "Seleccionar" },
  disabled: { type: Boolean, default: false },
  allowCustom: { type: Boolean, default: false },
  maxlength: { type: Number, default: undefined },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const rootRef = ref(null);
const search = ref("");
const searchInputRef = ref(null);
const activeIndex = ref(0);
const optionRefs = ref([]);

const selectedLabel = computed(
  () =>
    (props.options.find((option) => option.value === props.modelValue)?.inputLabel ||
    props.options.find((option) => option.value === props.modelValue)?.label) ||
    "",
);
const filteredOptions = computed(() => {
  const term = normalizeText(search.value);
  if (!term) return props.options;
  return props.options.filter((option) =>
    normalizeText(option.label).includes(term),
  );
});

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function selectOption(value) {
  emit("update:modelValue", value);
  open.value = false;
}

function onCustomInput(event) {
  search.value = event.target.value;
  open.value = true;
  emit("update:modelValue", event.target.value);
  activeIndex.value = -1;
}

function onCustomEnter(event) {
  if (!open.value) return;
  event.preventDefault();
  if (activeIndex.value >= 0) selectActiveOption();
  else open.value = false;
}

function onFocusOut(event) {
  if (props.allowCustom && !rootRef.value?.contains(event.relatedTarget)) open.value = false;
}

function openSelect() {
  if (props.disabled) return;
  open.value = true;
}

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el;
}

function scrollActiveOptionIntoView() {
  nextTick(() => {
    optionRefs.value[activeIndex.value]?.scrollIntoView({
      block: "nearest",
    });
  });
}

function moveActive(step) {
  if (!filteredOptions.value.length) return;
  const lastIndex = filteredOptions.value.length - 1;
  activeIndex.value =
    activeIndex.value + step < 0
      ? lastIndex
      : activeIndex.value + step > lastIndex
      ? 0
      : activeIndex.value + step;
  scrollActiveOptionIntoView();
}

function selectActiveOption() {
  const option = filteredOptions.value[activeIndex.value];
  if (!option) return;
  selectOption(option.value);
}

watch(open, async (value) => {
  if (!value) {
    search.value = "";
    activeIndex.value = 0;
    return;
  }
  activeIndex.value = props.allowCustom ? -1 : Math.max(
    0,
    filteredOptions.value.findIndex((option) => option.value === props.modelValue),
  );
  await nextTick();
  searchInputRef.value?.focus();
  scrollActiveOptionIntoView();
});

watch(filteredOptions, () => {
  optionRefs.value = [];
  activeIndex.value = props.allowCustom ? -1 : 0;
});

function onDocClick(event) {
  if (!rootRef.value?.contains(event.target)) open.value = false;
}

onMounted(() => {
  document.addEventListener("mousedown", onDocClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocClick);
});
</script>

<style scoped>
.select-fade-enter-active,
.select-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.select-fade-enter-from,
.select-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.select-field-menu {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.26) transparent;
}

.select-field-menu::-webkit-scrollbar {
  width: 6px;
}

.select-field-menu::-webkit-scrollbar-track {
  background: transparent;
}

.select-field-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.24);
  border-radius: 999px;
}
</style>
